import { z } from "zod";
/** A class — a group of children one teacher teaches (ADR-012). */
export const ClassSchema = z.object({
    classId: z.string().uuid(),
    name: z.string().min(1),
    students: z.number().int().nonnegative(),
});
/**
 * How much a teacher may open, and how much of it they have used.
 *
 * Set per teacher from the operator panel and enforced on the server, so this is a copy for the screen
 * rather than the rule — the client uses it to stop offering a button whose only outcome is a refusal,
 * which is the same reason the roster hides "close class" when only one is left.
 *
 * **Usage travels with the limit.** A screen given only the ceiling would have to count the classes it
 * happens to be holding and guess at the children, and the guess would be wrong for exactly the teacher
 * it matters to: children in a closed class still count, and no class list contains them.
 */
export const AllowanceSchema = z.object({
    classes: z.number().int().nonnegative(),
    classLimit: z.number().int().nonnegative(),
    students: z.number().int().nonnegative(),
    studentLimit: z.number().int().nonnegative(),
});
/**
 * The three kinds of account (ADR-011, ADR-013).
 *
 * All three sign in the same way — a phone number and a one-time code — so the role is not a
 * different authentication path. It is an authorisation fact and a routing fact: a teacher
 * lands on their class, a student lands on the games, an admin lands on the operator surface, and
 * none of them can reach another's.
 *
 * **`admin` is not a teacher with extra powers**, and the distinction is load-bearing. An admin has no
 * class, no roster and no learner record, so every class-scoped route refuses them for the same reason it
 * refuses a stranger: they own nothing. What they have instead is a surface that reads across all
 * classes and writes an audit row on the way through.
 *
 * The previous note here argued against a third role on the grounds that nothing needed one and it
 * would be unused branching. Something needs one now — deciding who may teach was a command-line job
 * with no record of who decided — so the branching is no longer unused. The cost the note predicted is
 * real and paid in `services/access.ts`, where every rule now says what an admin may do rather than
 * leaving it to be inferred.
 *
 * There is no self-registration for any of them. A student is created by their teacher, a teacher by an
 * approved application, and an admin only by somebody holding the database credentials.
 */
export const RoleSchema = z.enum(["teacher", "student", "admin"]);
/**
 * A teacher's gender, as they state it on their own application.
 *
 * Asked because an Iranian preschool is commonly staffed and grouped by it, and the person reading
 * the queue is deciding about a real staffroom rather than about a row — the same reason the school's
 * name is asked for. It is copied onto the account at approval, so the answer somebody gave and the
 * answer held against them cannot drift apart.
 *
 * **Nothing in the product branches on it.** No game, no goal, no comparison and no measurement reads
 * this; a value that changed what a child was shown would be a claim about children this product does
 * not make. It is a fact about the adult, kept where the adult's other details are.
 *
 * Two values, and not a free string. A field an operator has to read as prose is a field that arrives
 * spelled six ways, and the whole use of it is being able to see a queue at a glance.
 */
export const GenderSchema = z.enum(["female", "male"]);
/**
 * Who is signed in, as the client sees it.
 *
 * `profileId` is present for a student and null for a teacher, because it is the learner
 * record every event is written against and a teacher never produces events. Keeping it in
 * this response is what lets the child's device start recording without a second request.
 */
export const CurrentUserSchema = z.object({
    userId: z.string().uuid(),
    role: RoleSchema,
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** The preschool a teacher works at. Null for a student. */
    schoolName: z.string().nullable(),
    profileId: z.string().uuid().nullable(),
    bundleVersion: z.string().nullable(),
    /**
     * A teacher's classes, and a student's own one.
     *
     * On the session rather than fetched separately because the shell cannot decide which screen to show
     * without it: a teacher with two classes needs a switcher before the first roster loads.
     */
    classes: z.array(ClassSchema).default([]),
    /**
     * A teacher's allowance, and null for anybody else.
     *
     * Beside the classes and for the same reason: the class screen has to know whether "new class" is on
     * offer before it draws itself, and a second request for that would leave the button flickering
     * between available and not.
     */
    allowance: AllowanceSchema.nullable().default(null),
});
/**
 * What happened when somebody submitted a valid one-time code (ADR-012).
 *
 * Verifying the code proves they hold the number. It no longer implies an account, because a teacher now
 * exists only after an application is approved and a student only after a teacher adds them to a class —
 * so the answer to "who is this" has four shapes, and each one leads somewhere different on screen.
 *
 * The three refusals are separate rather than one "no account", because the next step differs in each:
 * apply, wait, or be told the decision went the other way. Collapsing them would leave somebody looking
 * at a registration form they have already filled in.
 */
export const SignInOutcomeSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("signed-in"), user: CurrentUserSchema }),
    /** Nobody has registered this number: a teacher applies, a child asks their teacher. */
    z.object({ kind: z.literal("not-registered") }),
    z.object({ kind: z.literal("application-pending") }),
    z.object({
        kind: z.literal("application-rejected"),
        /** The decision in the decider's own words, if they left any. It will be asked about. */
        note: z.string().nullable(),
    }),
]);
//# sourceMappingURL=accounts.js.map